import { canManagePurchaseInvoiceImports } from '../modules/invoices/invoiceAccessPolicy';
import api from './api';
import { clearAuthLocalState } from './authStorage';
import {
  AuthPermissionUser,
  hasUserRole,
  isAdminUser,
  resolveUserCapability,
  RoleInput,
} from './authPermissions';

export type { UserRole } from './authPermissions';

export const AUTH_USER_CHANGED_EVENT = 'flowship-auth-user-changed';

export interface User extends AuthPermissionUser {
  id: string;
  email: string;
  name: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

interface VerifyResponse {
  valid: boolean;
  user: User;
  expiresIn: number;
  expiresAt: string;
}

// Track token refresh state
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
// Store timeout ID for cleanup - use number type for browser environment
let refreshTimeoutId: number | null = null;

const persistUser = (user: User) => {
  localStorage.setItem('user', JSON.stringify(user));
  window.dispatchEvent(new CustomEvent(AUTH_USER_CHANGED_EVENT, { detail: user }));
};

const readStoredUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

export const authService = {
  async login(data: LoginData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', data);
    localStorage.setItem('token', response.data.token);
    persistUser(response.data.user);
    
    // Start token refresh check after login
    this.scheduleTokenRefresh();
    
    return response.data;
  },

  logout() {
    clearAuthLocalState();

    // Cancel any scheduled token refresh
    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId);
      refreshTimeoutId = null;
    }
    
    window.location.href = '/login';
  },

  getCurrentUser(): User | null {
    return readStoredUser();
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  },

  hasRole(role: RoleInput): boolean {
    return hasUserRole(this.getCurrentUser(), role);
  },

  canViewFinancials(): boolean {
    return this.canViewFinancialReports();
  },

  canViewCustomerPayments(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canViewCustomerPayments', this.canViewMargin());
  },

  canEditCustomerOrders(): boolean {
    return this.hasRole(['ADMIN', 'OPERATIONS', 'ORDER_STAFF', 'TESTER']);
  },

  canViewDashboardFullKpis(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canViewDashboardFullKpis', isAdminUser(user) && this.canViewMargin());
  },

  canManageSupplierOrders(): boolean {
    return this.hasRole(['ADMIN', 'OPERATIONS']);
  },

  canManageOrderCosts(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canManageOrderCosts', isAdminUser(user));
  },

  canViewCustomerOrderFallbackCosts(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(
      user,
      'canViewCustomerOrderFallbackCosts',
      this.canManageOrderCosts() || this.hasRole(['ADMIN', 'OPERATIONS'])
    );
  },

  canEditCustomerOrderFallbackCosts(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(
      user,
      'canEditCustomerOrderFallbackCosts',
      this.canManageOrderCosts() || this.hasRole(['ADMIN', 'OPERATIONS'])
    );
  },

  canManagePurchaseInvoices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canManagePurchaseInvoices', isAdminUser(user));
  },

  /**
   * Invoice import workflow gate (purchase/sales drafts, upload, extraction).
   * Fail-closed like the backend allowlist: no ADMIN fallback, the server must
   * explicitly grant the capability.
   */
  canManageInvoiceImports(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canManageInvoiceImports', false);
  },

  canManagePurchaseInvoiceImports(): boolean {
    return canManagePurchaseInvoiceImports(this.getCurrentUser());
  },

  canViewInvoiceDocuments(): boolean {
    return this.canViewSalesDocuments() || this.canViewPurchaseDocuments();
  },

  canViewSalesDocuments(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canViewSalesDocuments', this.canViewCustomerSalePrices());
  },

  canViewPurchaseDocuments(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canViewPurchaseDocuments', this.canManagePurchaseInvoices());
  },

  canViewFinancialReports(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canViewFinancialReports', this.canViewMargin());
  },

  canViewCustomerSalePrices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canViewCustomerSalePrices', this.canViewMargin());
  },

  canEditCustomerSalePrices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canEditCustomerSalePrices', this.canViewCustomerSalePrices());
  },

  canViewSupplierPurchasePrices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(
      user,
      'canViewSupplierPurchasePrices',
      this.canViewMargin() || this.canManagePurchaseInvoices()
    );
  },

  canEditSupplierPurchasePrices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canEditSupplierPurchasePrices', this.canViewSupplierPurchasePrices());
  },

  // Catalog average purchase cost (owner decision 2026-09-23): ADMIN and
  // OPERATIONS maintain it; the backend predicate is the security boundary.
  canViewProductAverageCost(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(
      user,
      'canViewProductAverageCost',
      hasUserRole(user, ['ADMIN', 'OPERATIONS']) || this.canViewSupplierPurchasePrices()
    );
  },

  canEditProductAverageCost(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(
      user,
      'canEditProductAverageCost',
      hasUserRole(user, ['ADMIN', 'OPERATIONS']) || this.canEditSupplierPurchasePrices()
    );
  },

  canViewSupplierOrderPurchasePdf(): boolean {
    return this.canManageSupplierOrders() || this.canViewSupplierPurchasePrices();
  },

  canEditWarehouseDocumentPurchasePrices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(
      user,
      'canEditWarehouseDocumentPurchasePrices',
      this.canViewWarehouseDocuments() || this.canEditSupplierPurchasePrices()
    );
  },

  canEditPZDocumentPurchasePrices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canEditPZDocumentPurchasePrices', this.canEditWarehouseDocumentPurchasePrices());
  },

  canViewWZDocumentSalePrices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(
      user,
      'canViewWZDocumentSalePrices',
      this.canViewWarehouseDocuments() || this.canViewCustomerSalePrices()
    );
  },

  canEditWZDocumentSalePrices(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(
      user,
      'canEditWZDocumentSalePrices',
      this.canViewWarehouseDocuments() || this.canEditCustomerSalePrices()
    );
  },

  canViewSupplierOrderPurchasePrices(): boolean {
    const user = this.getCurrentUser();
    if (this.canManageSupplierOrders()) {
      return true;
    }
    return resolveUserCapability(user, 'canViewSupplierOrderPurchasePrices', this.canViewSupplierPurchasePrices());
  },

  canEditSupplierOrderPurchasePrices(): boolean {
    const user = this.getCurrentUser();
    if (this.canManageSupplierOrders()) {
      return true;
    }
    return resolveUserCapability(user, 'canEditSupplierOrderPurchasePrices', this.canEditSupplierPurchasePrices());
  },

  canViewSupplierOrderFinance(): boolean {
    const user = this.getCurrentUser();
    if (this.canManageSupplierOrders()) {
      return true;
    }
    return resolveUserCapability(user, 'canViewSupplierOrderFinance', this.canViewSupplierPurchasePrices());
  },

  canEditSupplierOrderFinance(): boolean {
    const user = this.getCurrentUser();
    if (this.canManageSupplierOrders()) {
      return true;
    }
    return resolveUserCapability(user, 'canEditSupplierOrderFinance', this.canEditSupplierPurchasePrices());
  },

  canViewTransportCosts(): boolean {
    const user = this.getCurrentUser();
    return user?.canViewTransportCosts === true;
  },

  canEditTransportCosts(): boolean {
    const user = this.getCurrentUser();
    return user?.canEditTransportCosts === true;
  },

  canViewWarehouseDocuments(): boolean {
    return this.hasRole(['ADMIN', 'OPERATIONS']);
  },

  // Warehouse-document purchase fields are visible to warehouse-document roles,
  // in addition to anyone with financial visibility. Scoped to the documents:
  // it does not widen customer sale prices, inventory COGS, supplier orders,
  // costs, margin, or reports. Mirrors backend warehouse-document predicates.
  // PZ download rides on the same predicate as PZ price visibility — the PZ pages
  // gate both columns and the download button on canViewWarehouseDocumentPurchasePrices.
  canViewWarehouseDocumentPurchasePrices(): boolean {
    return this.canViewWarehouseDocuments() || this.canViewSupplierPurchasePrices();
  },

  canDownloadWZDocumentPdf(): boolean {
    return this.canViewWarehouseDocuments()
      || (this.canViewCustomerSalePrices() && this.canViewSupplierPurchasePrices());
  },

  canViewDocumentCenter(): boolean {
    return this.canViewWarehouseDocuments() || this.canViewInvoiceDocuments();
  },

  canViewMargin(): boolean {
    const user = this.getCurrentUser();
    return resolveUserCapability(user, 'canViewMargin', isAdminUser(user));
  },
  
  async refreshToken(): Promise<void> {
    // If already refreshing, return the existing promise
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }
    
    isRefreshing = true;
    refreshPromise = (async () => {
      try {
        const response = await api.post<AuthResponse>('/auth/refresh');
        localStorage.setItem('token', response.data.token);
        persistUser(response.data.user);
        console.log('Token refreshed successfully');
        
        // Schedule next refresh
        this.scheduleTokenRefresh();
      } catch (error: any) {
        console.error('Failed to refresh token:', error);
        // If refresh fails with 401, the interceptor will handle redirect
        throw error;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();
    
    return refreshPromise;
  },
  
  async verifyToken(): Promise<VerifyResponse | null> {
    try {
      const response = await api.get<VerifyResponse>('/auth/verify');
      persistUser(response.data.user);
      return response.data;
    } catch (error) {
      console.error('Token verification failed:', error);
      return null;
    }
  },
  
  // Schedule automatic token refresh before expiration
  scheduleTokenRefresh() {
    // Cancel any existing timeout
    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId);
    }
    
    // Verify token and schedule refresh
    this.verifyToken().then(result => {
      if (result && result.valid) {
        // Calculate when to refresh (5 minutes before expiration, or at 80% of lifetime)
        const expiresIn = result.expiresIn * 1000; // Convert to milliseconds
        const refreshTime = Math.min(
          expiresIn - (5 * 60 * 1000), // 5 minutes before expiration
          expiresIn * 0.8 // 80% of token lifetime
        );
        
        if (refreshTime > 0) {
          console.log(`Token expires in ${Math.floor(expiresIn / 1000)} seconds, scheduling refresh in ${Math.floor(refreshTime / 1000)} seconds`);
          refreshTimeoutId = window.setTimeout(() => {
            this.refreshToken().catch(error => {
              console.error('Auto token refresh failed:', error);
            });
          }, refreshTime);
        }
      }
    });
  }
};

// Check and schedule token refresh on app startup if user is logged in
if (authService.isAuthenticated()) {
  authService.scheduleTokenRefresh();
}
